import { useState } from "react";
import api from "@/lib/api";
import { errMsg } from "@/lib/hsUtil";

const Row = ({ label, value, testid }) => (
  <div className="border-b border-neutral-800 py-2" data-testid={testid}>
    <div className="text-xs text-silver uppercase tracking-wider">{label}</div>
    <div>{value || "Ask the site supervisor"}</div>
  </div>
);

const StepSite = ({ data }) => {
  const s = data.site;
  return (
    <div data-testid="induction-step-site">
      <div className="text-3xl font-bold text-red-500 mb-2" data-testid="induction-111">Emergency: call 111</div>
      <Row label="Site address" value={data.project.address} testid="induction-address"/>
      <Row label="Site supervisor" value={[s.supervisor_name, s.supervisor_phone].filter(Boolean).join(" · ")} testid="induction-supervisor"/>
      <Row label="Emergency warning signal" value={s.emergency_signal} testid="induction-signal"/>
      <Row label="Assembly point" value={s.assembly_point} testid="induction-assembly"/>
      <Row label="First aid kit" value={s.first_aid} testid="induction-first-aid"/>
      <Row label="Fire extinguisher" value={s.fire_extinguisher} testid="induction-extinguisher"/>
      <Row label="Nearest medical centre" value={s.medical_centre} testid="induction-medical"/>
    </div>
  );
};

const StepHazards = ({ data }) => (
  <div className="space-y-2" data-testid="induction-step-hazards">
    {data.hazards.length === 0 && <div className="opacity-70" data-testid="induction-no-hazards">
      {data.hazards_setup ? "The site supervisor will brief you on today's hazards."
        : "Hazards for this project have not been set up yet. The site supervisor will brief you on the hazards before you start."}</div>}
    {data.hazards.map((h, i) => (
      <div key={i} className="border border-neutral-800 rounded p-3" data-testid={`induction-hazard-${i}`}>
        <div className="font-semibold">{h.hazard}</div>
        {h.controls && <div className="text-sm opacity-80 mt-1"><span className="text-silver">Controls: </span>{h.controls}</div>}
      </div>
    ))}
  </div>
);

const StepRules = ({ data }) => (
  <ul className="space-y-2" data-testid="induction-step-rules">
    {data.rules.map((r, i) => <li key={i} className="border-l-2 border-silver pl-3">{r}</li>)}
  </ul>
);

const Question = ({ q, idx, value, wrong, onPick }) => (
  <div className={`border rounded p-3 ${wrong ? "border-red-500" : "border-neutral-800"}`} data-testid={`quiz-q-${idx}`}>
    <div className="mb-2">{idx + 1}. {q.q}</div>
    {q.options.map((o, oi) => (
      <label key={oi} className="flex items-start gap-2 py-1.5 cursor-pointer">
        <input type="radio" name={`q${idx}`} checked={value === oi} onChange={() => onPick(oi)} className="mt-1" data-testid={`quiz-q-${idx}-opt-${oi}`}/>
        <span>{o}</span>
      </label>
    ))}
    {wrong && <div className="text-red-400 text-sm" data-testid={`quiz-q-${idx}-wrong`}>Not quite. Try again.</div>}
  </div>
);

const StepQuiz = ({ data, code, defaultName, onFinish, busy }) => {
  const [answers, setAnswers] = useState(data.quiz.map(() => null));
  const [wrong, setWrong] = useState([]);
  const [passed, setPassed] = useState(false);
  const [fit, setFit] = useState(false);
  const [sig, setSig] = useState(defaultName || "");
  const [err, setErr] = useState("");
  const pick = (i, v) => { setPassed(false); setWrong(w => w.filter(x => x !== i)); setAnswers(a => a.map((x, j) => j === i ? v : x)); };
  async function check() {
    setErr("");
    if (answers.some(a => a === null)) { setErr("Answer every question."); return; }
    try {
      const { data: r } = await api.post(`/site/${code}/quiz`, { answers });
      setWrong(r.wrong); setPassed(r.ok);
      if (!r.ok) setErr("Some answers are wrong. Fix them to continue.");
    } catch (e) { setErr(errMsg(e)); }
  }
  return (
    <div className="space-y-3" data-testid="induction-step-quiz">
      {data.quiz.map((q, i) => <Question key={i} q={q} idx={i} value={answers[i]} wrong={wrong.includes(i)} onPick={v => pick(i, v)}/>)}
      {!passed && <button className="btn-silver w-full py-3" onClick={check} data-testid="quiz-check-btn">Check answers</button>}
      {passed && (
        <div className="card space-y-3" data-testid="fit-declaration">
          <div className="text-green-400" data-testid="quiz-passed">All correct.</div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={fit} onChange={e => setFit(e.target.checked)} className="mt-1" data-testid="fit-for-work-check"/>
            <span>I am fit for work. I am not affected by alcohol, drugs or fatigue.</span>
          </label>
          <input className="input w-full" placeholder="Type your full name to sign" value={sig} onChange={e => setSig(e.target.value)} data-testid="induction-signature"/>
          <button className="btn-silver w-full py-3" disabled={busy || !fit || sig.trim().length < 2}
            onClick={() => onFinish({ answers, fit_for_work: fit, signature: sig })} data-testid="induction-sign-in-btn">Sign in</button>
        </div>
      )}
      {err && <div className="text-red-400 text-sm" data-testid="quiz-error">{err}</div>}
    </div>
  );
};

const TITLES = ["This site", "Top hazards", "Site rules", "Quick check"];

export const Induction = ({ data, code, defaultName, onFinish, onCancel, busy }) => {
  const [i, setI] = useState(0);
  const Body = [StepSite, StepHazards, StepRules][i];
  return (
    <div className="card space-y-4" data-testid="site-induction">
      <div className="flex items-center justify-between">
        <div className="text-silver text-lg" data-testid="induction-title">Site induction · {TITLES[i]}</div>
        <div className="text-xs opacity-60" data-testid="induction-progress">{i + 1} of 4</div>
      </div>
      {i < 3 ? <Body data={data}/> : <StepQuiz data={data} code={code} defaultName={defaultName} onFinish={onFinish} busy={busy}/>}
      <div className="flex gap-2">
        <button className="btn-ghost flex-1 py-3" onClick={() => (i === 0 ? onCancel() : setI(i - 1))} data-testid="induction-back-btn">Back</button>
        {i < 3 && <button className="btn-silver flex-1 py-3" onClick={() => setI(i + 1)} data-testid="induction-next-btn">Next</button>}
      </div>
    </div>
  );
};
